import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  SimpleChanges,
  Self,
} from '@angular/core';
import { NgControl, Validators, ValidatorFn } from '@angular/forms';
import { ControlValueAccessor, FormControl } from '@ngneat/reactive-forms';
import { combineLatest, BehaviorSubject, Observable, Subject } from 'rxjs';
import { delay, map, switchMap, takeUntil } from 'rxjs/operators';

import {
  DisposalSite,
  DisposalSiteModel,
} from '@digital-platform/disposal-sites/domain';
import { DisposalSitesFacadeService } from '@digital-platform/disposal-sites/services';
import { allStateCodes } from '@digital-platform/shared/domain';

interface DisposalSiteGroup {
  readonly title: string;
  readonly disposalSites: DisposalSite[];
  readonly test: (disposalSite: DisposalSite) => boolean;
  readonly subGroups?: DisposalSiteGroups;
}

interface DisposalSiteGroups {
  [group: string]: DisposalSiteGroup;
}
@Component({
  selector: 'digital-platform-disposal-site-radio-select',
  templateUrl: './radio-select.component.html',
  styleUrls: ['./radio-select.component.scss'],
})
export class RadioSelectComponent
  implements OnInit, OnChanges, OnDestroy, ControlValueAccessor<DisposalSite>
{
  @Input() public type: string;
  @Input() public asbestosType: 'FRIABLE' | 'NONFRIABLE' | 'NONE';
  @Input() profileStatus: string;

  value: DisposalSite;
  disposalSites: DisposalSite[] = [];
  myControl = new FormControl<string>();
  private originalValidators: ValidatorFn[] = [];

  groups$: Observable<DisposalSiteGroups>;

  protected changes$ = new Subject<DisposalSite>();
  protected touch$ = new Subject<boolean>();
  protected destroyed$ = new Subject<void>();

  private type$ = new BehaviorSubject<string>(null);
  private asbestosType$ = new BehaviorSubject<
    'FRIABLE' | 'NONFRIABLE' | 'NONE'
  >('NONE');
  private value$ = new BehaviorSubject<string>(null);
  private disposalSites$: Observable<DisposalSite[]>;

  constructor(
    @Optional()
    @Self()
    public controlDir: NgControl,
    private readonly facade: DisposalSitesFacadeService
  ) {
    if (this.controlDir !== null) {
      this.controlDir.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    if (this.controlDir !== null) {
      // ensures validators and validation state gets handled properly, feels a little hacky
      const control = this.controlDir.control;
      let myValidators = [this.validate];
      if (control.validator) {
        this.originalValidators.push(control.validator);
        myValidators = [control.validator, ...myValidators];
        this.myControl.setValidators(control.validator);
        this.myControl.updateValueAndValidity({ emitEvent: false });
      }
      control.setValidators(myValidators);
      control.updateValueAndValidity();
      const markAllAsTouched = control.markAllAsTouched.bind(control);
      control.markAllAsTouched = () => {
        markAllAsTouched();
        this.myControl.markAllAsTouched();
      };
    }

    this.loadDisposalSites();

    combineLatest([this.value$, this.disposalSites$])
      .pipe(
        map(([value, disposalSites]) => {
          const found = disposalSites.find((d) => d.id === value);

          return found || null;
        })
      )
      .subscribe((changes) => {
        this.value = changes;
        this.changes$.next(changes);
      });
  }

  onChange($event) {
    this.myControl.setValue($event.value);
    this.value$.next($event.value);
  }

  public ngOnChanges(changes: SimpleChanges) {
    this.type$.next(this.type);
    this.asbestosType$.next(this.asbestosType);
    if (
      changes.profileStatus?.currentValue &&
      changes.profileStatus?.currentValue === 'Not Submitted'
    ) {
      this.myControl.removeValidators(Validators.required);
      this.myControl.updateValueAndValidity();

      const control = this.controlDir.control;

      control.removeValidators(Validators.required);
      control.updateValueAndValidity();
    } else if (
      changes.profileStatus?.currentValue &&
      changes.profileStatus?.currentValue !== 'Not Submitted'
    ) {
      this.myControl.addValidators(Validators.required);
      this.myControl.updateValueAndValidity();

      const control = this.controlDir.control;
      control.addValidators(Validators.required);
      control.updateValueAndValidity();
    }
  }

  public ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.changes$.complete();
    this.touch$.complete();

    // restore validators
    if (this.controlDir && this.originalValidators) {
      this.controlDir.control.setValidators(this.originalValidators);
    }
  }

  public writeValue(value) {
    if (value) {
      this.myControl.setValue(value, { emitEvent: false });
    } else {
      this.myControl.reset();
    }
    this.value$.next(value?.id || null);
  }

  public registerOnChange(fn: (value: DisposalSite) => void) {
    this.changes$.pipe(takeUntil(this.destroyed$)).subscribe(fn);
  }

  public onTouched = () => {};
  public registerOnTouched(fn: () => void) {
    this.touch$.pipe(takeUntil(this.destroyed$)).subscribe(fn);
  }

  private loadDisposalSites() {
    this.disposalSites$ = this.type$.pipe(
      // delay here ensures this runs async as it can cause issues by rendering before
      // this.value is set (the radio for the selected value may not be selected properly)
      delay(1),
      switchMap((type) => {
        if (type === 'deepwell') {
          return combineLatest([
            this.facade.deepWellSites$,
            this.asbestosType$,
          ]);
        }

        return combineLatest([this.facade.disposalSites$, this.asbestosType$]);
      }),
      map(([disposalSites, asbestosType]) =>
        disposalSites.map((disposalSite) => {
          let disabledReason = '';

          if (
            asbestosType === 'NONFRIABLE' &&
            !disposalSite.isNonFriableAsbestosAccepted
          ) {
            disabledReason =
              'This landfill does not accept nonfriable asbestos';
          } else if (
            asbestosType === 'FRIABLE' &&
            !disposalSite.isFriableAsbestosAccepted
          ) {
            disabledReason = 'This landfill does not accept friable asbestos';
          }

          return {
            ...disposalSite,
            disabledReason,
          };
        })
      )
    );

    this.groups$ = combineLatest([this.disposalSites$, this.type$]).pipe(
      map<[DisposalSite[], string], [DisposalSite[], DisposalSiteGroups]>(
        ([disposalSites, type]) => {
          const filteredByType = disposalSites.filter((disposalSite) => {
            if (type) {
              return disposalSite.type.toLowerCase() === type.toLowerCase();
            }

            // no type set
            return true;
          });

          const states = filteredByType.reduce((stateSet, disposalSite) => {
            stateSet.add(disposalSite.address.stateCode);

            return stateSet;
          }, new Set<string>());

          let groupsForType: DisposalSiteGroups = {
            default: {
              title: 'Disposal Sites',
              disposalSites: [],
              test: () => true,
            },
          };

          if (this.type === 'landfill') {
            groupsForType = this.getLandfillGroups(Array.from(states));
          } else if (this.type === 'deepwell') {
            groupsForType = this.getDeepwellGroups();
          }

          return [filteredByType, groupsForType];
        }
      ),
      map(([disposalSites, groupsForType]) => {
        this.disposalSites = disposalSites;

        // use reduce to sort out what goes in what group, this is a little janky
        // TODO: refactor this to be cleaner, maybe recursive? Not very elegant
        return disposalSites.reduce((groups, disposalSite) => {
          for (const group in groups) {
            if (groups.hasOwnProperty(group)) {
              if (groups[group].subGroups) {
                for (const subGroup in groups[group].subGroups) {
                  if (groups[group].subGroups.hasOwnProperty(subGroup)) {
                    if (groups[group].subGroups[subGroup].test(disposalSite)) {
                      groups[group].subGroups[subGroup].disposalSites.push(
                        disposalSite
                      );
                      continue;
                    }
                  }
                }
              }
              if (groups[group].test(disposalSite)) {
                groups[group].disposalSites.push(disposalSite);
                continue;
              }
            }
          }

          return groups;
        }, groupsForType);
      })
    );
  }

  private getLandfillGroups(states?: string[]): DisposalSiteGroups {
    if (states && states.length > 0) {
      return states
        .map((stateCode) => {
          const state = allStateCodes.find((s) => s.code === stateCode)?.label;
          let subGroups: DisposalSiteGroups;

          if (stateCode === 'TX') {
            subGroups = {
              classOneAndTwo: {
                title: 'Class One and Two',
                disposalSites: [],
                test: (disposalSite) =>
                  disposalSite.address.stateCode === stateCode &&
                  disposalSite.class.includes('class-one') &&
                  disposalSite.class.includes('class-two'),
              },
              classTwoOnly: {
                title: 'Class Two Only',
                disposalSites: [],
                test: (disposalSite) =>
                  disposalSite.address.stateCode === stateCode &&
                  !disposalSite.class.includes('class-one') &&
                  disposalSite.class.includes('class-two'),
              },
              others: {
                title: 'Other Disposal Locations',
                disposalSites: [],
                test: (disposalSite) =>
                  disposalSite.address.stateCode === stateCode &&
                  !disposalSite.class.includes('class-two'),
              },
            };
          }

          return <DisposalSiteGroups>{
            [stateCode]: {
              title: `${state} Disposal Locations`,
              disposalSites: [],
              test: (disposalSite) =>
                disposalSite.address.stateCode === stateCode,
              subGroups,
            },
          };
        })
        .reduce((groups, item) => ({ ...groups, ...item }), {});
    }

    return null;
  }

  private getDeepwellGroups(): DisposalSiteGroups {
    return {
      hazardous: {
        title: 'Hazardous',
        disposalSites: [],
        test: (disposalSite) => disposalSite.isHazardousAccepted,
      },
      nonHazardous: {
        title: 'Non-Hazardous',
        disposalSites: [],
        test: (disposalSite) => !disposalSite.isHazardousAccepted,
      },
    };
  }

  validate = (c) => {
    this.myControl.updateValueAndValidity({
      onlySelf: false,
      emitEvent: false,
    });

    if (this.myControl.valid) {
      return null;
    }

    return this.myControl.errors;
  };
}
